import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface InvoiceProduct {
  id?: string;
  productName: string;
  model?: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface InvoiceData {
  // 請求書情報
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  
  // 宛先情報
  companyName: string;
  companyAddress?: string;
  companyPostalCode?: string;
  
  // 工事情報
  constructionTitle: string;
  constructionNumber?: string;
  
  // 製品情報
  products: InvoiceProduct[];
  
  // 金額情報
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  
  // 備考
  notes?: string;
}

export interface CompanyInfo {
  name: string;
  postalCode?: string;
  address?: string;
  phone?: string;
  fax?: string;
  email?: string;
  bankInfo?: string;
}

// デフォルトの自社情報
const DEFAULT_COMPANY_INFO: CompanyInfo = {
  name: '株式会社セカイエ',
  postalCode: '〒000-0000',
  address: '東京都○○区○○ 0-0-0',
  phone: 'TEL: 00-0000-0000',
  fax: 'FAX: 00-0000-0000',
  email: 'info@sekaie.co.jp',
  bankInfo: '○○銀行 ○○支店 普通 0000000',
};

// 金額をフォーマット
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
  }).format(amount);
}

// 日付をフォーマット
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
}

// HTMLテンプレートを生成
function createInvoiceHTML(data: InvoiceData, companyInfo: CompanyInfo): string {
  const productsRows = data.products.map(product => `
    <tr>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${product.productName}</td>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${product.model || '-'}</td>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${product.quantity}</td>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${formatCurrency(product.unitPrice)}</td>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${formatCurrency(product.amount)}</td>
    </tr>
  `).join('');

  return `
    <div id="invoice-content" style="width: 794px; padding: 40px; font-family: 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif; background: white; color: #333;">
      <!-- ヘッダー -->
      <div style="text-align: center; margin-bottom: 30px;">
        <h1 style="font-size: 32px; margin: 0; color: #2563eb;">請求書</h1>
        <p style="font-size: 14px; color: #666; margin-top: 5px;">INVOICE</p>
      </div>

      <!-- 請求書番号と日付 -->
      <div style="text-align: right; margin-bottom: 20px;">
        <p style="margin: 5px 0; font-size: 14px;"><strong>請求書番号:</strong> ${data.invoiceNumber}</p>
        <p style="margin: 5px 0; font-size: 14px;"><strong>請求日:</strong> ${formatDate(data.invoiceDate)}</p>
        ${data.dueDate ? `<p style="margin: 5px 0; font-size: 14px;"><strong>お支払期限:</strong> ${formatDate(data.dueDate)}</p>` : ''}
      </div>

      <!-- 宛先 -->
      <div style="margin-bottom: 30px;">
        <p style="font-size: 18px; font-weight: bold; margin: 0 0 10px 0; border-bottom: 2px solid #2563eb; padding-bottom: 5px; display: inline-block;">
          ${data.companyName} 御中
        </p>
        ${data.companyPostalCode ? `<p style="margin: 5px 0; font-size: 14px; color: #666;">${data.companyPostalCode}</p>` : ''}
        ${data.companyAddress ? `<p style="margin: 5px 0; font-size: 14px; color: #666;">${data.companyAddress}</p>` : ''}
      </div>

      <!-- 工事情報 -->
      <div style="background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
        <p style="margin: 0; font-size: 14px;"><strong>件名:</strong> ${data.constructionTitle}</p>
        ${data.constructionNumber ? `<p style="margin: 5px 0 0 0; font-size: 14px; color: #666;">工事番号: ${data.constructionNumber}</p>` : ''}
      </div>

      <!-- 請求金額 -->
      <div style="background: #2563eb; color: white; padding: 20px; border-radius: 8px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: center;">
        <span style="font-size: 16px;">ご請求金額（税込）</span>
        <span style="font-size: 28px; font-weight: bold;">${formatCurrency(data.totalAmount)}</span>
      </div>

      <!-- 明細テーブル -->
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead>
          <tr style="background: #2563eb; color: white;">
            <th style="border: 1px solid #2563eb; padding: 10px; text-align: left; width: 35%;">品名</th>
            <th style="border: 1px solid #2563eb; padding: 10px; text-align: center; width: 15%;">型番</th>
            <th style="border: 1px solid #2563eb; padding: 10px; text-align: center; width: 10%;">数量</th>
            <th style="border: 1px solid #2563eb; padding: 10px; text-align: right; width: 20%;">単価</th>
            <th style="border: 1px solid #2563eb; padding: 10px; text-align: right; width: 20%;">金額</th>
          </tr>
        </thead>
        <tbody>
          ${productsRows}
        </tbody>
      </table>

      <!-- 合計 -->
      <div style="display: flex; justify-content: flex-end; margin-bottom: 30px;">
        <table style="width: 300px;">
          <tr>
            <td style="padding: 8px; text-align: left; color: #666;">小計</td>
            <td style="padding: 8px; text-align: right;">${formatCurrency(data.subtotal)}</td>
          </tr>
          <tr>
            <td style="padding: 8px; text-align: left; color: #666;">消費税（${data.taxRate}%）</td>
            <td style="padding: 8px; text-align: right;">${formatCurrency(data.taxAmount)}</td>
          </tr>
          <tr style="border-top: 2px solid #333;">
            <td style="padding: 8px; text-align: left; font-weight: bold;">合計</td>
            <td style="padding: 8px; text-align: right; font-size: 18px; font-weight: bold; color: #2563eb;">${formatCurrency(data.totalAmount)}</td>
          </tr>
        </table>
      </div>

      ${data.notes ? `
      <!-- 備考 -->
      <div style="background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 30px;">
        <p style="margin: 0 0 10px 0; font-weight: bold; font-size: 14px;">備考</p>
        <p style="margin: 0; font-size: 14px; white-space: pre-wrap;">${data.notes}</p>
      </div>
      ` : ''}

      <!-- 区切り線 -->
      <hr style="border: none; border-top: 1px solid #ddd; margin: 30px 0;" />

      <!-- 発行者情報 -->
      <div style="text-align: right;">
        <p style="margin: 5px 0; font-weight: bold; font-size: 16px;">${companyInfo.name}</p>
        ${companyInfo.postalCode ? `<p style="margin: 5px 0; font-size: 14px; color: #666;">${companyInfo.postalCode}</p>` : ''}
        ${companyInfo.address ? `<p style="margin: 5px 0; font-size: 14px; color: #666;">${companyInfo.address}</p>` : ''}
        ${companyInfo.phone ? `<p style="margin: 5px 0; font-size: 14px; color: #666;">${companyInfo.phone}</p>` : ''}
        ${companyInfo.email ? `<p style="margin: 5px 0; font-size: 14px; color: #666;">${companyInfo.email}</p>` : ''}
      </div>

      ${companyInfo.bankInfo ? `
      <!-- 振込先 -->
      <div style="margin-top: 20px; text-align: right;">
        <p style="margin: 5px 0; font-weight: bold; font-size: 14px;">お振込先</p>
        <p style="margin: 5px 0; font-size: 14px; color: #666;">${companyInfo.bankInfo}</p>
      </div>
      ` : ''}
    </div>
  `;
}

// 請求書PDFを生成
export async function generateInvoicePDF(
  data: InvoiceData,
  companyInfo: CompanyInfo = DEFAULT_COMPANY_INFO
): Promise<{ blob: Blob; base64: string }> {
  // HTMLを生成
  const html = createInvoiceHTML(data, companyInfo);

  // 一時的なコンテナを作成
  const container = document.createElement('div');
  container.innerHTML = html;
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  const invoiceElement = container.querySelector('#invoice-content') as HTMLElement;

  try {
    const canvasPromise = html2canvas(invoiceElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('PDF生成がタイムアウトしました（30秒）')), 30000)
    );
    const canvas = await Promise.race([canvasPromise, timeoutPromise]);

    // A4サイズでPDFを作成
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // 画像のアスペクト比を維持しながらA4に収める
    const imgWidth = pageWidth - 20; // 左右10mmずつマージン
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // ページに収まるように調整
    let finalWidth = imgWidth;
    let finalHeight = imgHeight;
    
    if (imgHeight > pageHeight - 20) {
      finalHeight = pageHeight - 20;
      finalWidth = (canvas.width * finalHeight) / canvas.height;
    }

    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 10, 10, finalWidth, finalHeight);

    // PDFをBlobとBase64で出力
    const blob = doc.output('blob');
    const base64 = doc.output('datauristring').split(',')[1];

    return { blob, base64 };
  } finally {
    // 一時的なコンテナを削除
    document.body.removeChild(container);
  }
}

// PDFをダウンロード
export function downloadInvoicePDF(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// PDFをプレビュー用のURLを生成
export function createPDFPreviewURL(blob: Blob): string {
  return URL.createObjectURL(blob);
}

// 製品リストから合計金額を計算
export function calculateInvoiceTotals(
  products: InvoiceProduct[],
  taxRate: number = 10
): { subtotal: number; taxAmount: number; totalAmount: number } {
  const subtotal = products.reduce((sum, product) => sum + product.amount, 0);
  const taxAmount = Math.floor(subtotal * (taxRate / 100));
  const totalAmount = subtotal + taxAmount;
  return { subtotal, taxAmount, totalAmount };
}
